// Detalhe de uma credencial: o que ela é, quem depende dela, e o que dá para fazer com cada
// dependente.
//
// É página, e não gaveta, por causa do trabalho principal daqui: consolidar credenciais
// duplicadas movendo vínculo a vínculo. Isso é uma sessão de trabalho — precisa de URL para
// voltar, compartilhar e não perder o contexto a cada diálogo que abre por cima.
//
// Usa o mesmo vocabulário do resto do produto (SectionCard/InfoItem de orders, subscriptions,
// webhooks): uma tela de administração que inventa a própria moldura parece outro sistema.
import {
  ArrowLeftIcon,
  KeyRoundIcon,
  LinkIcon,
  PlugZapIcon,
  ServerOffIcon,
  ShieldCheckIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { CertificateStatusBadge } from "@/components/commons/certificate-status-badge"
import { ProviderForm } from "@/components/commons/provider-form"
import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { DashboardContainer } from "@/components/layout/dashboard-container"
import { Header } from "@/components/layout/header"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { FRONTEND_ROUTES } from "@/constants"
import { MoveConfigDialog } from "@/features/merchant-processors/components/move-config-dialog"
import { ProcessorCertificateSelect } from "@/features/merchant-processors/components/processor-certificate-select"
import { ProcessorLinkedConfigs } from "@/features/merchant-processors/components/processor-linked-configs"
import { useWhiteLabels } from "@/hooks/use-white-labels"
import {
  useMerchantProcessor,
  useTestMerchantProcessorConnection,
  useUpdateMerchantProcessor,
} from "@/services/admin/merchant-processors"
import {
  useActivateProcessorConfigMutation,
  useDeactivateProcessorConfigMutation,
  useDeleteProcessorConfigMutation,
  useProcessorConfigMetadata,
} from "@/services/admin/processor-config"
import type { MerchantProcessorLinkedConfig } from "@/types/admin-merchant-processor"

const prefix = "pages.adminMerchantProcessors"

export function MerchantProcessorDetailPage() {
  const { t } = useTranslation()
  const { processorId = "" } = useParams()

  const {
    data: processor,
    isLoading,
    isError,
    refetch,
  } = useMerchantProcessor(processorId)
  const { data: metadata } = useProcessorConfigMetadata()
  const { data: whiteLabels } = useWhiteLabels()

  const [name, setName] = useState<string | null>(null)
  // Certificado escolhido mas ainda NAO salvo. Entra no mesmo Salvar das credenciais: trocar o
  // certificado de uma credencial muda o handshake de todos os meios que a usam, e isso nao pode
  // acontecer no clique de um combo.
  const [certificateId, setCertificateId] = useState<
    string | null
  >(null)
  const [moving, setMoving] =
    useState<MerchantProcessorLinkedConfig | null>(null)
  const [deleting, setDeleting] =
    useState<MerchantProcessorLinkedConfig | null>(null)

  const { mutate: update, isPending: saving } =
    useUpdateMerchantProcessor(processorId, () => {
      setName(null)
      setCertificateId(null)
    })
  const {
    mutate: testConnection,
    data: testResult,
    isPending: testing,
  } = useTestMerchantProcessorConnection()
  const activate = useActivateProcessorConfigMutation()
  const deactivate = useDeactivateProcessorConfigMutation()
  const { mutate: removeConfig } =
    useDeleteProcessorConfigMutation(() =>
      setDeleting(null)
    )

  const gatewayMetadata = metadata?.find(
    (item) => item.gateway === processor?.gateway
  )
  const whiteLabelName = (id: string) =>
    (Array.isArray(whiteLabels) ? whiteLabels : []).find(
      (item) => item.uuid === id
    )?.name ?? id

  const currentName = name ?? processor?.name ?? ""
  const isNameDirty =
    name !== null && name.trim() !== (processor?.name ?? "")

  const currentCertificateId =
    certificateId ?? processor?.certificate?.id ?? ""
  const isCertificateDirty =
    certificateId !== null &&
    certificateId !== (processor?.certificate?.id ?? "")

  return (
    <DashboardContainer>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            { label: t("sidebar.administration") },
            {
              href: FRONTEND_ROUTES
                .ADMIN_MERCHANT_PROCESSORS.href,
              label: t("sidebar.merchantProcessors"),
            },
            { label: processor?.name ?? processorId },
          ]}
        />
      </Header>

      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">
              {processor?.name ??
                t(`${prefix}.detail.title`)}
            </h1>
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>
                {gatewayMetadata?.label ??
                  processor?.gateway ??
                  ""}
              </span>
              <span
                aria-hidden="true"
                className="text-border"
              >
                ·
              </span>
              <span className="font-mono text-xs">
                {processorId}
              </span>
              {processor?.certificate ? (
                <CertificateStatusBadge
                  status={processor.certificate.status}
                  daysToExpire={
                    processor.certificate.daysToExpire
                  }
                />
              ) : null}
            </p>
          </div>
          <Button asChild variant="outline">
            <Link
              to={
                FRONTEND_ROUTES.ADMIN_MERCHANT_PROCESSORS
                  .href
              }
            >
              <ArrowLeftIcon
                className="mr-2 size-4"
                aria-hidden="true"
              />
              {t(`${prefix}.detail.back`)}
            </Link>
          </Button>
        </div>

        {isError ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
            <ServerOffIcon
              className="size-8 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground">
              {t(`${prefix}.loadError`)}
            </p>
            <Button
              variant="outline"
              onClick={() => refetch()}
            >
              {t(`${prefix}.retry`)}
            </Button>
          </div>
        ) : isLoading || !processor ? (
          <div
            data-testid="processor-detail-skeleton"
            className="grid gap-4 @4xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]"
          >
            <Skeleton className="h-80 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        ) : (
          <div className="grid gap-4 @4xl:grid-cols-[minmax(0,1fr)_minmax(0,26rem)]">
            <div className="space-y-4">
              <SectionCard
                title={t(`${prefix}.detail.credentials`)}
                icon={<KeyRoundIcon className="size-4" />}
              >
                {gatewayMetadata ? (
                  <ProviderForm
                    fields={gatewayMetadata.fields}
                    currentValues={processor.fields}
                    mode="edit"
                    saving={saving}
                    // O nome vive DENTRO do form: antes ele gravava sozinho ao perder
                    // o foco, e uma tela de credencial não pode salvar sem que alguém
                    // tenha mandado salvar.
                    headerDirty={
                      isNameDirty || isCertificateDirty
                    }
                    onDiscard={() => {
                      setName(null)
                      setCertificateId(null)
                    }}
                    header={
                      <div className="max-w-md space-y-2">
                        <Label htmlFor="processor-name">
                          {t(`${prefix}.detail.name`)}
                        </Label>
                        <Input
                          id="processor-name"
                          value={currentName}
                          maxLength={255}
                          readOnly={saving}
                          onChange={(event) =>
                            setName(event.target.value)
                          }
                        />
                        <p className="text-xs text-muted-foreground">
                          {t(`${prefix}.detail.nameHint`)}
                        </p>

                        {/* So' os bancos com mTLS: oferecer o combo num adquirente
                            prometeria um vinculo que o backend nao aceita. */}
                        {gatewayMetadata.supportsCertificate && (
                          <ProcessorCertificateSelect
                            value={currentCertificateId}
                            disabled={saving}
                            onChange={setCertificateId}
                          />
                        )}
                      </div>
                    }
                    onSubmit={(provider) =>
                      update({
                        ...(isNameDirty
                          ? { name: currentName.trim() }
                          : {}),
                        // O certificado viaja no `provider`, como o metadata declara
                        // (campo CERTIFICATE_REF) — o backend resolve a entrada do cofre
                        // pelo merchant e recusa a de outro.
                        ...(Object.keys(provider).length >
                          0 || isCertificateDirty
                          ? {
                              provider: {
                                ...provider,
                                ...(isCertificateDirty
                                  ? {
                                      mtlsCertificateId:
                                        currentCertificateId,
                                    }
                                  : {}),
                              },
                            }
                          : {}),
                      })
                    }
                    footerStart={
                      gatewayMetadata.supportsTestConnection ? (
                        <Button
                          type="button"
                          variant="outline"
                          disabled={testing}
                          onClick={() =>
                            testConnection(processor.id)
                          }
                        >
                          <PlugZapIcon
                            className="mr-2 size-4"
                            aria-hidden="true"
                          />
                          {t(
                            `${prefix}.detail.testConnection`
                          )}
                        </Button>
                      ) : undefined
                    }
                  />
                ) : null}

                {testResult ? (
                  <p
                    role="status"
                    aria-live="polite"
                    className={
                      testResult.success
                        ? "mt-3 text-sm text-emerald-700"
                        : "mt-3 text-sm text-red-700"
                    }
                  >
                    {testResult.success
                      ? t(`${prefix}.detail.testOk`, {
                          latency:
                            testResult.latencyMs ?? 0,
                        })
                      : (testResult.detail ??
                        t(`${prefix}.detail.testFailed`))}
                  </p>
                ) : null}
              </SectionCard>
            </div>

            <div className="space-y-4">
              <SectionCard
                title={t(`${prefix}.detail.identity`)}
                icon={
                  <ShieldCheckIcon className="size-4" />
                }
              >
                <div className="grid gap-3 @sm:grid-cols-2">
                  <InfoItem
                    label={t(`${prefix}.detail.id`)}
                    value={processor.id}
                    copyable
                    truncate
                  />
                  <InfoItem
                    label={t(`${prefix}.detail.gateway`)}
                    value={
                      gatewayMetadata?.label ??
                      processor.gateway
                    }
                  />
                  <InfoItem
                    label={t(`${prefix}.detail.methods`)}
                    value={processor.methods
                      ?.map((method) =>
                        t(
                          `pages.adminProcessorConfig.methods.${method}`,
                          { defaultValue: method }
                        )
                      )
                      .join(", ")}
                  />
                  <InfoItem
                    label={t(
                      `${prefix}.detail.certificate`
                    )}
                    value={
                      processor.certificate?.id ?? undefined
                    }
                    copyable={Boolean(
                      processor.certificate?.id
                    )}
                    truncate
                  />
                </div>
              </SectionCard>

              <SectionCard
                title={t(`${prefix}.detail.linkedConfigs`)}
                icon={<LinkIcon className="size-4" />}
                action={
                  <Badge
                    variant="outline"
                    className="border-blue-200 bg-blue-50 font-normal tabular-nums text-blue-700"
                  >
                    {processor.linkedConfigsCount}
                  </Badge>
                }
              >
                <p className="mb-3 text-xs text-muted-foreground">
                  {t(`${prefix}.detail.linkedConfigsHint`)}
                </p>
                <ProcessorLinkedConfigs
                  configs={processor.linkedConfigs ?? []}
                  whiteLabelName={whiteLabelName}
                  onMove={setMoving}
                  onToggleActive={(config) =>
                    config.active
                      ? deactivate.mutate(config.configId)
                      : activate.mutate(config.configId)
                  }
                  onDelete={setDeleting}
                />
              </SectionCard>
            </div>
          </div>
        )}
      </section>

      <MoveConfigDialog
        config={moving}
        currentProcessorId={processorId}
        gateway={processor?.gateway ?? ""}
        onOpenChange={(open) => {
          if (!open) setMoving(null)
        }}
      />

      <AlertDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => {
          if (!open) setDeleting(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${prefix}.detail.deleteDialog.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.active
                ? t(
                    `${prefix}.detail.deleteDialog.mustDeactivate`
                  )
                : t(
                    `${prefix}.detail.deleteDialog.description`
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${prefix}.detail.deleteDialog.cancel`)}
            </AlertDialogCancel>
            {/* Ativa não some daqui: o backend recusa com 409, e oferecer o
                botão só levaria o operador a um erro que ele não podia prever. */}
            {!deleting?.active && (
              <AlertDialogAction
                onClick={() => {
                  if (deleting)
                    removeConfig(deleting.configId)
                }}
              >
                {t(`${prefix}.detail.deleteDialog.confirm`)}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardContainer>
  )
}
